import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useSettingsStore } from "../state/useSettingsStore";
import { parseBBCode, countVisibleChars } from "../engine/bbcode";
import { RichText } from "./RichText";

const isTouchDevice = typeof navigator !== "undefined" && navigator.maxTouchPoints > 0;

interface DialogueBoxProps {
  text: string;
  speaker?: string | null;
  speakerColor?: string | null;
  onAdvance: () => void;
  skipMode?: boolean;
  skipSeen?: boolean;
  autoMode?: boolean;
}

export function DialogueBox({ text, speaker, speakerColor, onAdvance, skipMode, skipSeen, autoMode }: DialogueBoxProps) {
  const textSpeed = useSettingsStore((s) => s.textSpeed);
  const nodes = useMemo(() => parseBBCode(text), [text]);
  const totalChars = useMemo(() => countVisibleChars(nodes), [nodes]);
  const [revealedCount, setRevealedCount] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const complete = revealedCount >= totalChars;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    setRevealedCount(0);
    clearTimer();

    if (skipMode || skipSeen) {
      setRevealedCount(totalChars);
      return;
    }

    if (totalChars === 0) return;

    const baseMs = 1000 / textSpeed;
    const getDelay = (charIndex: number): number => {
      const ch = text[charIndex] ?? "";
      if (".!?…".includes(ch)) return Math.round(baseMs * 3);
      if (",:;".includes(ch)) return Math.round(baseMs * 1.5);
      return Math.round(baseMs);
    };

    let current = 0;
    const tick = () => {
      current += 1;
      setRevealedCount(current);
      if (current < totalChars) {
        timerRef.current = setTimeout(tick, getDelay(current));
      } else {
        timerRef.current = null;
      }
    };
    timerRef.current = setTimeout(tick, getDelay(0));

    return clearTimer;
  }, [text, totalChars, textSpeed, skipMode, skipSeen, clearTimer]);

  const handleClick = () => {
    if (!complete) {
      clearTimer();
      setRevealedCount(totalChars);
    } else {
      onAdvance();
    }
  };

  return (
    <div
      onClick={handleClick}
      className="theme-panel"
      style={{
        position: "absolute",
        bottom: "4.5rem",
        left: "50%",
        transform: "translateX(-50%)",
        width: "min(80%, 720px)",
        padding: "1.5rem 2rem 2.2rem 2rem",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      {speaker && (
        <p
          style={{
            margin: "0 0 0.5rem 0",
            fontSize: "0.95rem",
            fontWeight: "bold",
            color: speakerColor ?? "#7ec8e3",
          }}
        >
          {speaker}
        </p>
      )}
      <p style={{ margin: 0, lineHeight: 1.7, fontSize: "1.1rem" }}>
        <RichText nodes={nodes} revealedCount={revealedCount} />
      </p>
      {complete && !skipMode && !skipSeen && !autoMode && (
        <span
          style={{
            position: "absolute",
            right: "2rem",
            bottom: "0.5rem",
            opacity: 0.4,
            fontSize: "0.8rem",
            pointerEvents: "none",
          }}
        >
          {isTouchDevice ? "Tap to continue" : "Click or press Space to continue"}
        </span>
      )}
    </div>
  );
}
